import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

export default function ReportsAdmin() {
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ Fetch all reports
  const fetchReports = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/admin/reports"); // ✅ FIXED endpoint
      if (res.data.success) {
        setReports(res.data.reports);
      }
    } catch (err) {
      console.error("❌ Fetch Reports Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // ✅ Update Report Status
  const handleStatusChange = async (id, status) => {
    try {
      const res = await axios.put(
        `https://api.speedfollowinfo.com/api/admin/report-status/${id}`,
        { status }
      );
      if (res.data.success) {
        setReports((prev) =>
          prev.map((r) => (r._id === id ? { ...r, status } : r))
        );
        toastMessage(`✅ Report marked as ${status}`);
      }
    } catch (err) {
      console.error("❌ Update Report Error:", err);
    }
  };

  // ✅ Delete Report
  const handleDelete = async (id) => {
    if (!window.confirm("⚠️ Are you sure to delete this report?")) return;
    try {
      const res = await axios.delete(
        `https://api.speedfollowinfo.com/api/admin/report/${id}` // ✅ FIXED endpoint
      );
      if (res.data.success) {
        setReports((prev) => prev.filter((r) => r._id !== id));
        toastMessage("🗑 Report deleted successfully");
      }
    } catch (err) {
      console.error("❌ Delete Report Error:", err);
    }
  };

  // ✅ Small toast message popup
  const toastMessage = (text) => {
    const msg = document.createElement("div");
    msg.textContent = text;
    msg.style.position = "fixed";
    msg.style.top = "20px";
    msg.style.right = "20px";
    msg.style.background = "#6c63ff";
    msg.style.color = "#fff";
    msg.style.padding = "10px 18px";
    msg.style.borderRadius = "8px";
    msg.style.fontWeight = "600";
    msg.style.boxShadow = "0 0 12px rgba(0,0,0,0.3)";
    msg.style.zIndex = "9999";
    document.body.appendChild(msg);
    setTimeout(() => msg.remove(), 2500);
  };

  // ✅ Filtered Reports
  const filtered = reports.filter(
    (r) =>
      r.userId?.username?.toLowerCase().includes(search.toLowerCase()) ||
      r.userId?.email?.toLowerCase().includes(search.toLowerCase()) ||
      r.subject?.toLowerCase().includes(search.toLowerCase()) ||
      r.message?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 min-h-screen bg-[#0b0f1a] text-white">
      <h2 className="text-2xl font-bold mb-5 flex items-center gap-2 text-[#f2a900]">
        <AlertTriangle size={24} /> User Reports Management
      </h2>

      {/* 🔍 Search */}
      <div className="mb-5 flex gap-3">
        <input
          type="text"
          placeholder="🔍 Search by username, email, or subject..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-[#1b2138] border border-gray-700 rounded-md px-3 py-2 text-white w-full md:w-1/3"
        />
      </div>

      {/* 🧾 Table */}
      <div className="overflow-x-auto rounded-lg shadow-lg border border-gray-700">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-[#111827] text-[#f2a900] uppercase">
            <tr>
              <th className="p-3">Username</th>
              <th className="p-3">Email</th>
              <th className="p-3">Subject</th>
              <th className="p-3">Message</th>
              <th className="p-3">Date</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center p-6 text-gray-400">
                  Loading reports...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-6 text-gray-400 italic">
                  No reports found
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r._id}
                  className="border-b border-gray-700 hover:bg-[#1f2937] transition"
                >
                  <td className="p-3">{r.userId?.username || "Unknown"}</td>
                  <td className="p-3">{r.userId?.email || "N/A"}</td>
                  <td className="p-3 font-semibold text-[#00eaff]">
                    {r.subject || "N/A"}
                  </td>
                  <td className="p-3 text-gray-300">
                    {r.message || "No message"}
                  </td>
                  <td className="p-3 text-gray-400">
                    {new Date(r.createdAt).toLocaleString("en-GB")}
                  </td>
                  <td className="p-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        r.status === "Solved"
                          ? "bg-green-500 text-black"
                          : "bg-yellow-500 text-black"
                      }`}
                    >
                      {r.status || "Pending"}
                    </span>
                  </td>
                  <td className="p-3 flex justify-center gap-3">
                    {r.status !== "Solved" && (
                      <button
                        onClick={() => handleStatusChange(r._id, "Solved")}
                        className="bg-green-500 hover:bg-green-600 text-black px-3 py-1 rounded-lg text-xs font-semibold"
                      >
                        ✅ Solve
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(r._id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-semibold"
                    >
                      🗑 Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}